import clsx from "clsx";
import { cva, VariantProps } from "cva";
import {
  type ChangeEvent,
  forwardRef,
  type TextareaHTMLAttributes,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import { Chip } from "../Chip";
import { CheckOvalIcon } from "../Icon";
import styles from "./index.module.css";
export const variants = cva({
  variants: {
    variant: {
      distant: styles.distant,
      "field-only": styles.fieldOnly,
    },
    error: {
      true: styles.error,
    },
    disabled: {
      true: styles.disabled,
    },
    completed: {
      true: styles.completed,
    },
  },
});

export type TextareaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "children"> &
  VariantProps<typeof variants> & {
    message?: string;
    label?: string;
    hideChip?: boolean;
  };

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      hideChip,
      required,
      disabled,
      completed,
      error,
      message = "\u00A0",
      id,
      className,
      variant,
      onChange,
      value,
      defaultValue,
      maxLength,
      rows = 2,
      ...props
    },
    ref
  ) => {
    const controlId = useId();
    // 空白だけのメッセージを説明として紐付けると、VoiceOver が「スペース」と読み上げるため紐付けない
    const describedBy = variant !== "field-only" && message.trim() !== "" ? `describe-${id ?? controlId}` : undefined;
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const isControlled = value !== undefined;
    // Formik ベースの画面は value、server action ベースの画面は defaultValue を使っており、
    // Textarea を両方の画面で使う必要があるためこの吸収を入れている。
    // count と自動リサイズの挙動を揃えるため、内部では 1 つの値に寄せて扱う。
    const [innerValue, setInnerValue] = useState(() => value ?? defaultValue ?? "");
    const count = innerValue ? innerValue.toString().length : 0;

    useEffect(() => {
      if (isControlled) {
        setInnerValue(value ?? "");
      }
    }, [isControlled, value]);

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLTextAreaElement>) => {
        const text = e.target.value;

        setInnerValue(text);

        if (onChange) {
          onChange(e);
        }

        const textarea = textareaRef.current;
        if (textarea) {
          // border分の高さを計算する
          const computed = window.getComputedStyle(textarea);
          const borderTop = parseFloat(computed.borderTopWidth || "0");
          const borderBottom = parseFloat(computed.borderBottomWidth || "0");

          textarea.style.height = "auto";
          textarea.style.height = textarea.scrollHeight + borderTop + borderBottom + "px";
        }
      },
      [onChange]
    );

    return (
      <div className={clsx(styles.base, variants({ variant }), className)}>
        <div className="relative">
          {variant !== "field-only" && !!label && (
            <label
              className={clsx(variants({ variant, error, disabled, completed }), styles.label)}
              htmlFor={id ?? controlId}
            >
              {label}
              {completed ? (
                <span className={styles.completedIcon}>
                  <CheckOvalIcon className="" size="s" color="secondary" aria-label="入力済み" />
                </span>
              ) : (
                !hideChip &&
                !disabled && (
                  <Chip
                    className={styles.labelChip}
                    color={required ? "caution" : "weak"}
                    text={required ? "必須" : "任意"}
                  />
                )
              )}
            </label>
          )}
          <textarea
            ref={(node) => {
              textareaRef.current = node;
              if (typeof ref === "function") {
                ref(node);
              } else if (ref) {
                ref.current = node;
              }
            }}
            id={id ?? controlId}
            disabled={disabled || completed}
            required={required}
            aria-invalid={!!error}
            className={clsx(variants({ completed, variant, error }), styles.textarea)}
            aria-describedby={describedBy}
            rows={rows}
            maxLength={maxLength}
            onChange={handleChange}
            value={innerValue}
            {...props}
          />
        </div>
        {variant !== "field-only" && (
          <div className="mt-1 flex justify-end gap-5 pl-[10px] pr-1">
            <p
              id={`describe-${id ?? controlId}`}
              className={clsx(styles.message, variants({ error, disabled, completed }))}
            >
              {message}
            </p>
            {!!maxLength && (
              <div className={styles.count}>
                {count}/{maxLength}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);
